/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-03 12:00:00
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';
import ThemeSetupScreenList from './screen.list';
import ThemeSetupScreenSetup from './screen.setup';
import ThemeSetupScreenRevert from './screen.revert';
import ConfirmRevertThemeModal from './confirm-revert-theme-modal';
import RevertThemeAllFeatureChoosingModal from 'src/components/views/theme-revert/theme-revert-form/revert-theme-all-feature-survey-modal';
import ContactOurTeamThemeRevertModal from 'src/components/views/theme-revert/theme-revert-form/contact-our-team-theme-revert-modal';
import FeedbackModal from './feedback-modal';
import FeedbackModalV3 from './feedback-modal-v3';
import ThemeRevertErrorModal from 'src/components/containers/common/theme-revert-error-modal';
import { getShop } from 'src/helpers';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';

type Props = {
  installStatus: string,
  installAppStatus: string,
  themeSetupV2: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = {
  toggleThemes: boolean,
  youtubeModal: boolean
};

export const TypeScreen = {
  List: 'List',
  Setup: 'Setup',
  Revert: 'Revert'
};

const keyDontShowSurveyV3Again = 'dontShowSurveyV3Again';

class ThemeSetup extends Component<ComposedProps, State> {
  state = {
    toggleThemes: false,
    youtubeModal: false,
    screen: TypeScreen.List,
    themeRevert: null,
    themeSelected: null,

    activeModalConfirmRevert: false,
    activeFeedbackModal: false,
    activeFeedbackModalV3: false,
    activeModalRevertThemeError: false,
    dismissFlagAll: false,
    dismissFlagSearch: false,
    dontShowSurveyV3Again: false, // for survey v3
    dataSetup: {},
    activeModalRevertThemeAllFeatureChoosing: false,
    activeModalContactOurTeamThemeRevert: false
  };

  ref: { current: Object } = React.createRef();

  handleThemeSetupChangeScreen = screen => {
    this.setState({
      screen
    });
  };

  handleRemoveThemeV3 = themeRevert => {
    const { dispatch, i18n } = this.props;
    this.handleThemeSetupChangeScreen(TypeScreen.Revert);
    dispatch(themeSetupV3Action.checkTheme(themeRevert.id, i18n));
    this.setState({
      themeRevert,
      dataSetup: { theme: themeRevert }
    });
  };

  handleUpgrade = theme => {
    this.setState({
      themeSelected: theme,
      screen: TypeScreen.Setup
    });
  };

  gotoThemeList = () => {
    this.setState({ screen: TypeScreen.List });
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  handleRemove = (theme: Object, removeAll: boolean) => {
    if (removeAll) {
      this.setState({
        activeModalRevertThemeAllFeatureChoosing: true,
        dataSetup: { theme, removeAll }
      });
    } else {
      this.setState({ dataSetup: { theme, removeAll }, activeModalConfirmRevert: true });
    }
  };

  componentDidMount() {
    const from = _.get(this.props, 'location.state.from', '');
    if (from === 'install-banner') {
      this.setState({ screen: TypeScreen.Setup });
    }

    let dismissAll = false;
    let dismissSearch = false;
    try {
      dismissAll = localStorage.getItem(`${getShop()}-dismissThemeRevertFeedback-All`);
    } catch (error) {}
    try {
      dismissSearch = localStorage.getItem(`${getShop()}-dismissThemeRevertFeedback-Search`);
    } catch (error) {}

    // If the banner All is dismissed, hide it
    if (dismissAll === 'true') {
      this.setState({ dismissFlagAll: true });
    }

    // If the banner Search is dismissed, hide it
    if (dismissSearch === 'true') {
      this.setState({ dismissFlagSearch: true });
    }

    let dontShowSurveyV3Again = false;
    try {
      dontShowSurveyV3Again = localStorage.getItem(`${getShop()}-${keyDontShowSurveyV3Again}`);
    } catch (error) {}

    // If the banner flag is true, do not show any survey v3 again
    if (dontShowSurveyV3Again === 'true') {
      this.setState({ dontShowSurveyV3Again: true });
    }
  }

  componentWillReceiveProps(nextProps) {
    // Show feedback modal if success: Theme V2
    if (
      !nextProps.revertThemeLoading &&
      this.props.revertThemeLoading &&
      nextProps.revertThemeSuccess
    ) {
      setTimeout(() => {
        this.setState({ activeFeedbackModal: true });
      }, 2000);
    }

    // Show feedback modal if success: Theme V3
    if (
      !nextProps.revertThemeV3Loading &&
      this.props.revertThemeV3Loading &&
      nextProps.revertThemeV3Success &&
      !this.state.dontShowSurveyV3Again
    ) {
      setTimeout(() => {
        this.setState({ activeFeedbackModalV3: true });
      }, 2000);
    }

    if (!nextProps.revertThemeManualContactLoading && this.props.revertThemeManualContactLoading) {
      this.setState({ activeModalContactOurTeamThemeRevert: false });
    }

    // Show error modal if failure
    if (
      !nextProps.revertThemeV3Loading &&
      this.props.revertThemeV3Loading &&
      nextProps.revertThemeV3Error !== undefined
    ) {
      this.setState({ activeModalRevertThemeError: true, activeModalConfirmRevert: false });
    }

    if (!nextProps.submitErrorLoading && this.props.submitErrorLoading) {
      this.setState({ activeModalRevertThemeError: false });
    }
  }

  handleCloseFeedbackModal = (dismissFlagAll: boolean, dismissFlagSearch: boolean) => {
    this.setState({ activeFeedbackModal: false, dataSetup: {} });

    // Insert data to local storage
    if (dismissFlagAll) {
      this.setState({ dismissFlagAll });
      try {
        localStorage.setItem(`${getShop()}-dismissThemeRevertFeedback-All`, 'true');
      } catch (error) {}
    }

    if (dismissFlagSearch) {
      this.setState({ dismissFlagSearch });
      try {
        localStorage.setItem(`${getShop()}-dismissThemeRevertFeedback-Search`, 'true');
      } catch (error) {}
    }
  };

  handleCloseFeedbackModalV3 = (dontShowSurveyV3Again: boolean) => {
    this.setState({ activeFeedbackModalV3: false, dataSetup: {} });
    if (dontShowSurveyV3Again) {
      this.setState({ dontShowSurveyV3Again });
      try {
        localStorage.setItem(`${getShop()}-${keyDontShowSurveyV3Again}`, 'true');
      } catch (error) {}
    }
  };

  handleRevert = () => {
    this.setState(
      {
        activeModalRevertThemeAllFeatureChoosing: false
      },
      () => {
        setTimeout(() => {
          this.setState({
            activeModalConfirmRevert: true
          });
        }, 500);
      }
    );
  };

  render() {
    const { screen, themeRevert, themeSelected } = this.state;
    const {
      activeModalConfirmRevert,
      activeFeedbackModal,
      activeFeedbackModalV3,
      activeModalRevertThemeError,
      activeModalRevertThemeAllFeatureChoosing,
      activeModalContactOurTeamThemeRevert,
      dataSetup,
      dismissFlagAll,
      dismissFlagSearch
    } = this.state;

    const isHasDataInDataSetup = _.get(dataSetup, 'theme', false);
    const removeAll = _.get(dataSetup, 'removeAll', false);
    const isShowableActiveFeedbackModal =
      isHasDataInDataSetup &&
      activeFeedbackModal &&
      ((removeAll && !dismissFlagAll) || (!removeAll && !dismissFlagSearch));

    const isShowableActiveFeedbackModalV3 = isHasDataInDataSetup && activeFeedbackModalV3;

    return (
      <div className="BC-Page BC-Page__Theme-Setup">
        <MainMenu menuId="menu-integration-theme-setup" />
        {screen === TypeScreen.List && (
          <ThemeSetupScreenList
            {...this.props}
            handleThemeSetupChangeScreen={this.handleThemeSetupChangeScreen}
            handleRemoveThemeV3={this.handleRemoveThemeV3}
            handleUpgrade={this.handleUpgrade}
            themeRevert={themeRevert}
            handleRemove={this.handleRemove}></ThemeSetupScreenList>
        )}
        {screen === TypeScreen.Setup && (
          <ThemeSetupScreenSetup
            {...this.props}
            handleThemeSetupChangeScreen={this.handleThemeSetupChangeScreen}
            themeSelected={themeSelected}
            gotoThemeList={this.gotoThemeList}></ThemeSetupScreenSetup>
        )}
        {screen === TypeScreen.Revert && (
          <ThemeSetupScreenRevert
            {...this.props}
            handleThemeSetupChangeScreen={this.handleThemeSetupChangeScreen}
            theme={themeRevert}></ThemeSetupScreenRevert>
        )}
        <div>
          {/* This modal showing for revert theme lib v1, v2 when customer click 
          Remove all filter and search features or Remove search feature only 
          With libv3, check modal at screen.revert
          */}
          <ConfirmRevertThemeModal
            dataSetup={dataSetup}
            active={activeModalConfirmRevert}
            handleChange={() => this.toggleChangeState('activeModalConfirmRevert')}
          />

          <FeedbackModal
            active={isShowableActiveFeedbackModal}
            dataSetup={dataSetup}
            themeRevert={themeRevert}
            handleCloseFeedbackModal={(dismissFlagAll, dismissFlagSearch) =>
              this.handleCloseFeedbackModal(dismissFlagAll, dismissFlagSearch)
            }
          />

          <FeedbackModalV3
            active={isShowableActiveFeedbackModalV3}
            dataSetup={dataSetup}
            themeRevert={themeRevert}
            handleCloseFeedbackModal={this.handleCloseFeedbackModalV3}
          />

          <RevertThemeAllFeatureChoosingModal
            active={activeModalRevertThemeAllFeatureChoosing}
            handleClose={() => this.toggleChangeState('activeModalRevertThemeAllFeatureChoosing')}
            handleRevert={this.handleRevert}
            handleRevertContactOurTeam={() => {
              this.toggleChangeState('activeModalContactOurTeamThemeRevert');
              this.toggleChangeState('activeModalRevertThemeAllFeatureChoosing');
            }}
          />
          <ContactOurTeamThemeRevertModal
            dataSetup={dataSetup}
            active={activeModalContactOurTeamThemeRevert}
            handleClose={() => this.toggleChangeState('activeModalContactOurTeamThemeRevert')}
          />
          <ThemeRevertErrorModal
            active={activeModalRevertThemeError}
            handleChange={() => this.toggleChangeState('activeModalRevertThemeError')}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { themeSetup, themeSetupV3, themeRevert } = state;
  const statusStore = _.get(state.oauth, 'subscription.status', '');

  const storeExpired =
    statusStore === 'expired' ||
    statusStore === 'exceeded' ||
    statusStore === 'regularExpired' ||
    statusStore === 'specialDiscountExpired';

  return {
    storeExpired,
    installStatus: _.get(state.install, 'installStatus', ''),
    installAppStatus: _.get(state.oauth.tenantApp, 'installStatus', ''),
    themeSetupV2: _.get(state.oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false),

    getThemesLoading: _.get(themeSetup, 'getThemesLoading', true),
    themes: _.get(themeSetup, 'themes', []),
    flow: _.get(state.sync, 'flow', {}),
    syncProcessLoading: _.get(state.sync, 'syncProcessLoading', false),
    revertThemeSuccess: _.get(themeRevert, 'success', false),
    revertThemeLoading: _.get(themeRevert, 'loading', false),
    revertThemeError: _.get(themeRevert, 'revertThemeError', undefined),
    revertThemeManualContactSuccess: _.get(themeRevert, 'manualContactSuccess', false),
    revertThemeManualContactLoading: _.get(themeRevert, 'manualContactLoading', false),
    revertThemeManualContactError: _.get(themeRevert, 'manualContactError', undefined),

    // This variable to check if remove theme lib v3
    revertThemeV3Loading: _.get(themeSetupV3, 'revertThemeloading', false),
    revertThemeV3Success: _.get(themeSetupV3, 'revertThemeSuccess', false),
    revertThemeV3Error: _.get(themeSetupV3, 'revertThemeError', undefined),
    revertThemeV3ShowModalSurvey: _.get(themeSetupV3, 'revertThemeV3ShowModalSurvey', false),
    revertThemeV3: _.get(themeSetupV3, 'revertTheme', {}),

    // This variable to check if upgrade theme to theme lib v3
    upgradeThemeLoading: _.get(themeSetupV3, 'upgradeThemeloading', false),
    upgradeThemeSuccess: _.get(themeSetupV3, 'upgradeThemeSuccess', false),
    upgradeThemeError: _.get(themeSetupV3, 'upgradeThemeError', undefined),
    upgradeTheme: _.get(themeSetupV3, 'upgradeTheme', {}),

    submitErrorLoading: _.get(themeSetup, 'submitErrorLoading', true)
  };
};

export default connect(mapStateToProps)(withI18n()(ThemeSetup));
